<!--
// app/components/pages/region-world-cup/BracketTablesWorldCup.vue
-->
<script setup lang="ts">
import type { Team } from "~/src/team/domain/models/Team";
import type { MatchCenterData } from "~/src/match/domain/models/Match";
import IconArrowBracketTablesWorldCup from "~/app/components/pages/region-world-cup/IconArrowBracketTablesWorldCup.vue";
import NewMatchItemBracketWorldCup from "~/app/components/pages/region-world-cup/NewMatchItemBracketWorldCup.vue";
import { useTeams } from "~/app/composables/teams";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { isPastDate } from "~/src/Shared/utils";

type Props = {
  seasonIdData: number;
  bracketData: MatchCenterData[];
  teamsObj: Record<string, Team>;
  bracketKey: number;
  isHomeComponent?: boolean;
  inOtherLeaguesHome?: boolean;
};

const props = defineProps<Props>();

const { teamArrToObjById } = useTeams();
const { currentSeasonTeams, currentSeason } = useCompetitionStore();
const { locale } = useI18n();

const firstRoundMatches = computed(() => {
  return props.bracketData && props.bracketData.length
    ? props.bracketData[0].matches.sort((a, b) =>
        a.metaInformation.matchName.localeCompare(b.metaInformation?.matchName)
      )
    : [];
});
const secondRoundWinnersMatches = computed(() => {
  return props.bracketData && props.bracketData.length
    ? props.bracketData[1].matches
        .sort((a, b) => Number(a.metaInformation.matchName) - Number(b.metaInformation.matchName))
        .filter(m => m.metaInformation?.previousRound === "winner")
    : [];
});
const secondRoundLosersMatches = computed(() => {
  return props.bracketData && props.bracketData.length
    ? props.bracketData[1].matches
        .sort((a, b) => Number(a.metaInformation.matchName) - Number(b.metaInformation.matchName))
        .filter(m => m.metaInformation?.previousRound === "loser")
    : [];
});

const round2Active = isPastDate("2025-06-04T09:00:00.000Z");
const lastChanceActive = isPastDate("2025-06-08T09:00:00.000Z");
const round16Active = isPastDate("2025-06-10T09:00:00.000Z");
const quarterFinalsActive = isPastDate("2025-06-12T09:00:00.000Z");
const finalsActive = isPastDate("2025-06-13T09:00:00.000Z");

const trophyImage = "/img/world-cup/kwc-trophy.png";
</script>

<template>
  <div
    v-if="bracketData && bracketData.length && Object.keys(teamsObj).length"
    id="tablesSlider"
    :key="bracketKey"
    :class="[
      'w-full overflow-x-scroll overflow-y-hidden hide-scrollbar bigms:px-4',
      { 'max-h-[633px]': isHomeComponent },
    ]"
  >
    <div
      :class="[
        'grid grid-cols-[auto_794px_376px_repeat(4,210px)] gap-x-20',
        { 'bigsm:-ms-32 lg:-ms-48 -mt-16': isHomeComponent },
      ]"
      :style="isHomeComponent ? 'scale: 0.7' : 'scale: 0.8'"
    >
      <!--Round 1-->
      <div class="round-col-container">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.round1") }}</h3>
        <div class="round-container-cols-2 kwc-border-color">
          <div class="flex flex-col items-center gap-4">
            <new-match-item-bracket-world-cup
              v-for="(match, i) in firstRoundMatches.slice(0, 8)"
              :key="match.id || i"
              :match="match"
              :teams-obj="teamsObj"
              :external-link="inOtherLeaguesHome"
            ></new-match-item-bracket-world-cup>
          </div>
          <div class="flex w-full justify-center px-5">
            <div class="w-full"></div>
            <div class="w-[2px] kwc-divider"></div>
          </div>
          <div class="flex flex-col items-center gap-4">
            <new-match-item-bracket-world-cup
              v-for="(match, i) in firstRoundMatches.slice(8, 16)"
              :key="match.id || i"
              :match="match"
              :teams-obj="teamsObj"
              :external-link="inOtherLeaguesHome"
            ></new-match-item-bracket-world-cup>
          </div>
        </div>
      </div>

      <!--Round 2-->
      <div class="round-col-container" :class="{ 'active-stage': round2Active }">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.round2") }}</h3>
        <div class="round-container-cols-2 kwc-border-color !mt-10 !mb-20 w-full">
          <div class="flex flex-col items-center gap-4">
            <new-match-item-bracket-world-cup
              v-for="(match, i) in secondRoundWinnersMatches.slice(0, 4)"
              :key="match.id || i"
              :match="match"
              :teams-obj="teamsObj"
              :external-link="inOtherLeaguesHome"
            ></new-match-item-bracket-world-cup>
          </div>
          <div class="flex w-full justify-center px-5">
            <div class="w-full"></div>
            <div class="w-[2px] kwc-divider"></div>
          </div>
          <div class="flex flex-col items-center gap-4">
            <new-match-item-bracket-world-cup
              v-for="(match, i) in secondRoundWinnersMatches.slice(4, 8)"
              :key="match.id || i"
              :match="match"
              :teams-obj="teamsObj"
              :external-link="inOtherLeaguesHome"
            ></new-match-item-bracket-world-cup>
          </div>

          <icon-arrow-bracket-tables-world-cup
            width-class="w-[calc(5rem-8px)]"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            text-indicator="1-0"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-[calc(5rem-8px)]"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            text-indicator="1-1"
            :right="locale !== 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-[517px]"
            bg-color="#D4B78E"
            top-position-class="top-3"
            text-indicator="2-0"
            :right="locale !== 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
        </div>

        <div class="p-[2px] linear-gradient-red w-full">
          <div class="round-container-cols-2 !border-none bg-black">
            <div class="flex flex-col items-center gap-4">
              <new-match-item-bracket-world-cup
                v-for="(match, i) in secondRoundLosersMatches.slice(0, 4)"
                :key="match.id || i"
                :match="match"
                :teams-obj="teamsObj"
                :external-link="inOtherLeaguesHome"
              ></new-match-item-bracket-world-cup>
            </div>
            <div class="flex w-full justify-center px-5">
              <div class="w-full"></div>
              <div class="w-[2px] kwc-divider"></div>
            </div>
            <div class="flex flex-col items-center gap-4">
              <new-match-item-bracket-world-cup
                v-for="(match, i) in secondRoundLosersMatches.slice(4, 8)"
                :key="match.id || i"
                :match="match"
                :teams-obj="teamsObj"
                :external-link="inOtherLeaguesHome"
              ></new-match-item-bracket-world-cup>
            </div>

            <icon-arrow-bracket-tables-world-cup
              width-class="w-[calc(5rem-8px)]"
              bg-color="#7E0900"
              top-position-class="top-1/2"
              text-indicator="0-1"
              :right="locale === 'ar'"
            ></icon-arrow-bracket-tables-world-cup>
            <icon-arrow-bracket-tables-world-cup
              width-class="w-[calc(5rem-8px)]"
              bg-color="#7E0900"
              top-position-class="top-1/2"
              text-indicator="1-1"
              :right="locale !== 'ar'"
            ></icon-arrow-bracket-tables-world-cup>
            <icon-arrow-bracket-tables-world-cup
              width-class="w-[calc(5rem-8px)]"
              bg-color="#7E0900"
              top-position-class="bottom-0"
              text-indicator="0-2"
              :right="locale !== 'ar'"
            ></icon-arrow-bracket-tables-world-cup>
          </div>
        </div>
      </div>

      <!--Last Chance-->
      <div class="round-col-container" :class="{ 'active-stage': lastChanceActive }">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.lastChance") }}</h3>
        <div class="p-[2px] w-full linear-gradient-red mb-12 mt-20">
          <div class="round-container !border-none bg-black">
            <new-match-item-bracket-world-cup
              v-for="(match, i) in bracketData[2].matches"
              :key="match.id || i"
              :match="match"
              :teams-obj="teamsObj"
              name-no-bold
              :external-link="inOtherLeaguesHome"
              is-last-chance-match
            ></new-match-item-bracket-world-cup>

            <icon-arrow-bracket-tables-world-cup
              width-class="w-[78px]"
              bg-color="#7E0900"
              top-position-class="top-full"
              text-indicator="1-2"
              vertical
            ></icon-arrow-bracket-tables-world-cup>
            <icon-arrow-bracket-tables-world-cup
              width-class="w-[calc(4rem-3px)]"
              bg-color="#D4B78E"
              top-position-class="top-1/2"
              text-indicator="2-1"
              is-gradient-bg="linear-gradient(90deg, #5C010A 0%, #DBBE93 50%, #AE8957 100%)"
              :right="locale !== 'ar'"
            ></icon-arrow-bracket-tables-world-cup>
          </div>
        </div>
        <div
          class="w-full linear-gradient-red font-['Archivo_Expanded'] flex justify-center items-center p-2"
        >
          OUT
        </div>
      </div>

      <!--octavos-->
      <div class="round-col-container" :class="{ 'active-stage': round16Active }">
        <h3 class="round-title kwc-color max-w-36">{{ $t("bracketKwc.turnsTitle.roundOf16") }}</h3>
        <div class="round-container small kwc-border-color bg-black">
          <new-match-item-bracket-world-cup
            v-for="(match, i) in bracketData[3].matches
              .filter(m => m.metaInformation?.match_group === '1')
              .sort((a, b) => Number(a.metaInformation?.order) - Number(b.metaInformation?.order))"
            :key="match.id || i"
            :match="match"
            :teams-obj="teamsObj"
            name-no-bold
            :external-link="inOtherLeaguesHome"
          ></new-match-item-bracket-world-cup>
        </div>

        <div class="round-container small kwc-border-color bg-black">
          <new-match-item-bracket-world-cup
            v-for="(match, i) in bracketData[3].matches
              .filter(m => m.metaInformation?.match_group === '2')
              .sort((a, b) => Number(a.metaInformation?.order) - Number(b.metaInformation?.order))"
            :key="match.id || i"
            :match="match"
            :teams-obj="teamsObj"
            name-no-bold
            :external-link="inOtherLeaguesHome"
          ></new-match-item-bracket-world-cup>
        </div>

        <div class="round-container small kwc-border-color bg-black">
          <new-match-item-bracket-world-cup
            v-for="(match, i) in bracketData[3].matches
              .filter(m => m.metaInformation?.match_group === '3')
              .sort((a, b) => Number(a.metaInformation?.order) - Number(b.metaInformation?.order))"
            :key="match.id || i"
            :match="match"
            :teams-obj="teamsObj"
            name-no-bold
            :external-link="inOtherLeaguesHome"
          ></new-match-item-bracket-world-cup>
        </div>

        <div class="round-container small kwc-border-color bg-black">
          <new-match-item-bracket-world-cup
            v-for="(match, i) in bracketData[3].matches
              .filter(m => m.metaInformation?.match_group === '4')
              .sort((a, b) => Number(a.metaInformation?.order) - Number(b.metaInformation?.order))"
            :key="match.id || i"
            :match="match"
            :teams-obj="teamsObj"
            name-no-bold
            :external-link="inOtherLeaguesHome"
          ></new-match-item-bracket-world-cup>
        </div>
      </div>

      <!--cuartos-->
      <div class="round-col-container" :class="{ 'active-stage': quarterFinalsActive }">
        <h3 class="round-title kwc-color max-w-36">
          {{ $t("bracketKwc.turnsTitle.quarterFinals") }}
        </h3>
        <div class="round-container small kwc-border-color">
          <new-match-item-bracket-world-cup
            v-for="(match, i) in bracketData[4].matches.filter(m => m.metaInformation?.match_group === '1')"
            :key="match.id || i"
            :match="match"
            :teams-obj="teamsObj"
            name-no-bold
            no-border
            :external-link="inOtherLeaguesHome"
          ></new-match-item-bracket-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-20"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-8"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale !== 'ar'"
            no-pointer
          ></icon-arrow-bracket-tables-world-cup>
        </div>

        <div class="round-container small kwc-border-color">
          <new-match-item-bracket-world-cup
            v-for="(match, i) in bracketData[4].matches.filter(m => m.metaInformation?.match_group === '2')"
            :key="match.id || i"
            :match="match"
            :teams-obj="teamsObj"
            name-no-bold
            no-border
            :external-link="inOtherLeaguesHome"
          ></new-match-item-bracket-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-24"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-8"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale !== 'ar'"
            no-pointer
          ></icon-arrow-bracket-tables-world-cup>
        </div>

        <div class="round-container small kwc-border-color">
          <new-match-item-bracket-world-cup
            v-for="(match, i) in bracketData[4].matches.filter(m => m.metaInformation?.match_group === '3')"
            :key="match.id || i"
            :match="match"
            :teams-obj="teamsObj"
            name-no-bold
            no-border
            :external-link="inOtherLeaguesHome"
          ></new-match-item-bracket-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-24"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-8"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale !== 'ar'"
            no-pointer
          ></icon-arrow-bracket-tables-world-cup>
        </div>

        <div class="round-container small kwc-border-color">
          <new-match-item-bracket-world-cup
            v-for="(match, i) in bracketData[4].matches.filter(m => m.metaInformation?.match_group === '4')"
            :key="match.id || i"
            :match="match"
            :teams-obj="teamsObj"
            name-no-bold
            no-border
            :external-link="inOtherLeaguesHome"
          ></new-match-item-bracket-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-24"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-8"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale !== 'ar'"
            no-pointer
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>

      <!--semis-->
      <div class="round-col-container">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.semifinals") }}</h3>
        <div class="round-container small kwc-border-color">
          <new-match-item-bracket-world-cup
            v-for="(match, i) in bracketData[5].matches.filter(m => m.metaInformation?.match_group === '1')"
            :key="match.id || i"
            :match="match"
            :teams-obj="teamsObj"
            name-no-bold
            no-border
            :external-link="inOtherLeaguesHome"
          ></new-match-item-bracket-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-10"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-8"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale !== 'ar'"
            no-pointer
          ></icon-arrow-bracket-tables-world-cup>

          <icon-arrow-bracket-tables-world-cup
            width-class="w-[185px]"
            bg-color="#D4B78E"
            top-position-class="top-1/2 !-left-[6.2rem]"
            no-pointer
            vertical-no-centered
          ></icon-arrow-bracket-tables-world-cup>
        </div>

        <div class="round-container small kwc-border-color">
          <new-match-item-bracket-world-cup
            v-for="(match, i) in bracketData[5].matches.filter(m => m.metaInformation?.match_group === '3')"
            :key="match.id || i"
            :match="match"
            :teams-obj="teamsObj"
            name-no-bold
            no-border
            :external-link="inOtherLeaguesHome"
          ></new-match-item-bracket-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-10"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-8"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale !== 'ar'"
            no-pointer
          ></icon-arrow-bracket-tables-world-cup>

          <icon-arrow-bracket-tables-world-cup
            width-class="w-[185px]"
            bg-color="#D4B78E"
            top-position-class="top-1/2 !-left-[6.2rem]"
            no-pointer
            vertical-no-centered
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>

      <!--final-->
      <div class="round-col-container" :class="{ 'active-stage': finalsActive }">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.final") }}</h3>
        <div class="round-container small kwc-border-color">
          <img
            :src="trophyImage"
            alt="Trophy KWC"
            width="180"
            height="200"
            class="absolute bottom-[91%] self-center"
          />
          <new-match-item-bracket-world-cup
            v-for="(match, i) in bracketData[6].matches"
            :key="match.id || i"
            :match="match"
            :teams-obj="teamsObj"
            name-no-bold
            no-border
            :external-link="inOtherLeaguesHome"
          ></new-match-item-bracket-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-10"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>

          <icon-arrow-bracket-tables-world-cup
            width-class="w-[370px]"
            bg-color="#D4B78E"
            top-position-class="top-1/2 !-left-[6.2rem]"
            no-pointer
            vertical-no-centered
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.round-title {
  @apply font-['Termina'] font-semibold text-xl text-center uppercase mb-3 !min-h-[55px] break-words;
}

.round-container {
  @apply border-2 px-5 py-7 flex flex-col items-center gap-4 my-auto relative bg-black w-full;
}

.round-container.small {
  @apply py-4;
}

.round-container-cols-2 {
  @apply border-2 px-5 py-7 gap-4 my-auto grid grid-cols-[1fr_auto_1fr] relative justify-items-center bg-black;
}

.round-col-container {
  @apply flex flex-col justify-center items-center;
}

.linear-gradient-red {
  background-image: linear-gradient(90deg, #54000a 0%, #b70007 32%, #d2283d 51%, #b70007 70%, #54000a 100%);
}

.kwc-divider {
  background-image: repeating-linear-gradient(
    175deg,
    #d4b78e,
    #d4b78e 12px,
    transparent 12px,
    transparent 24px,
    #d4b78e 22px
  );
}

</style>
